import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Rocket, BookMarked } from "lucide-react";
import { voiceLine } from "@/lib/companionVoice";
import { currentTier, reducedMotion } from "@/lib/juice";
import { playCue } from "@/lib/soundCues";
import { track } from "@/lib/track";

/**
 * Full-screen celebration for the "wow" moments only (levelup / continent /
 * passport). Listens to `we-celebrate`. Skippable, never blocks > 1 s, and
 * matures by tier: Seedlings = large + emoji rain, Explorers = medium,
 * Pioneers = compact achievement card with what it means.
 */
const HEADLINES = {
  levelup:   { title: "Level Up!",           Icon: Rocket,     tint: "mwq-card-tint-reward" },
  continent: { title: "Continent Complete!", Icon: Trophy,     tint: "mwq-card-tint-success" },
  passport:  { title: "Passport Stamp!",     Icon: BookMarked, tint: "mwq-card-tint-success" },
};
const PIONEER_TITLE = { levelup: "New level reached", continent: "Continent complete", passport: "Passport updated" };
const CONFETTI_EMOJI = ["⭐", "🎉", "🌍", "✨", "🏆", "🎈", "🌟"];
const TIER_MS = { seedlings: 3600, explorers: 3000, pioneers: 2200 };

function makeParticles(n) {
  return Array.from({ length: n }, (_, i) => ({ id: i, x: Math.random() * 100, y: -10 - Math.random() * 30, rotate: Math.random() * 360, delay: Math.random() * 0.4, duration: 1.6 + Math.random() * 1.2, emoji: CONFETTI_EMOJI[i % CONFETTI_EMOJI.length], size: 18 + Math.random() * 16 }));
}

export default function CelebrationOverlay() {
  const [active, setActive] = useState(null);
  const [particles, setParticles] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    const onEvent = (e) => {
      const kind = e?.detail?.kind;
      if (!HEADLINES[kind]) return;
      const tier = currentTier();
      const line = voiceLine(kind === "levelup" ? "levelup" : "discovery", { tier });
      setActive({ ...e.detail, tier, line });
      setParticles(tier === "pioneers" || reducedMotion() ? [] : makeParticles(tier === "seedlings" ? 50 : 30));
      playCue("badge");
      track("celebration_shown", { kind, tier, major: 1 });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { setActive(null); track("celebration_completed", { kind, skipped: 0 }); }, TIER_MS[tier] || 3000);
    };
    window.addEventListener("we-celebrate", onEvent);
    return () => { window.removeEventListener("we-celebrate", onEvent); clearTimeout(timer.current); };
  }, []);

  const skip = () => { if (!active) return; clearTimeout(timer.current); track("celebration_completed", { kind: active.kind, skipped: 1 }); setActive(null); };
  const meta = active ? HEADLINES[active.kind] : null;
  const pio = active?.tier === "pioneers";
  const seed = active?.tier === "seedlings";
  const subtitle = active?.kind === "levelup"
    ? `${active.title || "Explorer"} · Level ${active.level || ""}`
    : active?.kind === "continent" ? `You explored every country in ${labelForContinent(active.continent)}.` : "";
  const meaning = pio && active?.kind === "levelup" ? "Levels reflect completed learning across subjects — not time spent." : pio && active?.kind === "continent" ? "Every country on this continent now has a passport record." : "";

  return (
    <AnimatePresence>
      {active && meta && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
          className={`fixed inset-0 z-[70] flex ${pio ? "items-end md:items-center pb-24 md:pb-0" : "items-start pt-20"} justify-center`} data-testid="celebration-overlay" onClick={skip} role="dialog" aria-label={meta.title}>
          <div className={`absolute inset-0 ${pio ? "bg-[#172B4D]/30" : "bg-[#172B4D]/45"}`} />
          {particles.length > 0 && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
              {particles.map((p) => (
                <motion.span key={p.id} initial={{ x: `${p.x}vw`, y: `${p.y}vh`, rotate: 0, opacity: 1 }} animate={{ y: "115vh", rotate: p.rotate * 2, opacity: [1, 1, 0.8] }} transition={{ duration: p.duration, delay: p.delay, ease: "easeIn" }} style={{ fontSize: p.size, position: "absolute", left: 0, top: 0 }}>{p.emoji}</motion.span>
              ))}
            </div>
          )}
          <motion.div initial={{ scale: pio ? 0.96 : 0.7, y: pio ? 12 : -24, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className={`relative mwq-card ${meta.tint} text-center mx-4 w-full ${pio ? "max-w-sm" : "max-w-md"} ${seed ? "py-7" : ""}`} data-testid="celebration-card" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center mb-2">
              <span className={`inline-flex items-center justify-center rounded-full bg-white ${seed ? "h-20 w-20" : pio ? "h-12 w-12" : "h-16 w-16"} ${reducedMotion() ? "" : "mwq-juice-correct"}`} style={{ color: "var(--color-reward-text)" }}>
                <meta.Icon size={seed ? 40 : pio ? 24 : 32} strokeWidth={2.5} />
              </span>
            </div>
            <h2 className={pio ? "mwq-card-title" : "mwq-section"} data-testid="celebration-title">{pio ? PIONEER_TITLE[active.kind] : meta.title}</h2>
            {subtitle && <p className="mt-1 mwq-body font-bold" data-testid="celebration-subtitle">{subtitle}</p>}
            {meaning && <p className="mt-1 mwq-meta" data-testid="celebration-meaning">{meaning}</p>}
            {active.line && !pio && <CompanionCheer line={active.line} large={seed} />}
            <button type="button" onClick={skip} data-testid="celebration-continue" className={`mt-4 ${pio ? "mwq-btn mwq-btn-secondary" : "mwq-btn mwq-btn-primary"}`}>{pio ? "Continue" : seed ? "Let's Go!" : "Continue"}</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function labelForContinent(id) {
  const names = { africa: "Africa", asia: "Asia", europe: "Europe", "north-america": "North America", "south-america": "South America", oceania: "Oceania", antarctica: "Antarctica" };
  return names[id] || id || "this continent";
}

function CompanionCheer({ line, large }) {
  const sp = line.speaker;
  return (
    <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.12, type: "spring", stiffness: 300, damping: 14 }}
      className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 border border-[var(--color-border)]" data-testid="celebration-companion">
      <span className={`inline-flex items-center justify-center rounded-full ${sp.color} ${large ? "h-10 w-10 text-2xl" : "h-8 w-8 text-xl"}`} aria-label={sp.name}>{sp.emoji}</span>
      <span className="mwq-meta text-left"><strong className="text-[var(--color-text)]">{sp.name}:</strong> {line.text}</span>
    </motion.div>
  );
}
